import { z } from 'zod';

import { correlationIdSchema, isoDateTimeSchema } from './primitives';

/**
 * Einheitliches Fehlerformat aller HTTP-Antworten beider Anwendungen.
 *
 * Angelehnt an RFC 9457 (Problem Details), aber bewusst schlanker. Wichtig ist
 * die Trennung von `code` und `message`: `code` ist maschinenlesbar und stabil,
 * `message` ist für Menschen und darf sich jederzeit ändern. Clients dürfen
 * niemals auf `message` verzweigen.
 */

/**
 * Stabile Fehlercodes. Werden vom Frontend und vom Worker ausgewertet und sind
 * damit Teil des öffentlichen Vertrags — Umbenennen ist ein Breaking Change.
 */
export const ERROR_CODES = {
  /** Eingabe hat die Schema-Validierung nicht bestanden. */
  validationFailed: 'VALIDATION_FAILED',
  /** Authentifizierung fehlt oder ist ungültig. */
  unauthenticated: 'UNAUTHENTICATED',
  /** Authentifiziert, aber nicht berechtigt. */
  forbidden: 'FORBIDDEN',
  /** Angeforderte Ressource existiert nicht. */
  notFound: 'NOT_FOUND',
  /** Zustandskonflikt (z. B. Node bereits registriert). */
  conflict: 'CONFLICT',
  /** Bootstrap-Token abgelaufen, verbraucht oder unbekannt. */
  enrollmentTokenInvalid: 'ENROLLMENT_TOKEN_INVALID',
  /** Protokollversion des Gegenübers wird nicht unterstützt. */
  protocolVersionUnsupported: 'PROTOCOL_VERSION_UNSUPPORTED',
  /** Kein Node erfüllt die Anforderungen der Platzierung. */
  noSchedulableNode: 'NO_SCHEDULABLE_NODE',
  /** Der angeforderte Blueprint steht im Katalog nicht zur Verfügung. */
  blueprintUnknown: 'BLUEPRINT_UNKNOWN',
  /**
   * Der Agent hat eine Spezifikation abgelehnt.
   *
   * Ausdrücklich ein eigener Code und nicht `VALIDATION_FAILED`: Dieser Fehler
   * ist **nicht** wiederholbar. Dieselbe Spezifikation wird auch beim fünften
   * Versuch abgelehnt, und ohne die Unterscheidung schickt die Queue sie
   * trotzdem fünfmal.
   */
  siteSpecRejected: 'SITE_SPEC_REJECTED',
  /** Für diese Site läuft bereits eine Operation. */
  operationConflict: 'OPERATION_CONFLICT',
  /** Die Domain ist bereits einer anderen Site zugeordnet. */
  domainAlreadyClaimed: 'DOMAIN_ALREADY_CLAIMED',
  /**
   * Die DNS-Vorprüfung ist fehlgeschlagen.
   *
   * Ausdrücklich ein eigener Code und keine allgemeine Validierung: Diese
   * Prüfung ist die Zugangskontrolle für Domains. Ohne sie trüge jemand eine
   * fremde Domain ein, finge deren Verkehr ab, und wir holten ein Zertifikat
   * für eine Domain, die uns nicht gehört.
   */
  dnsVerificationFailed: 'DNS_VERIFICATION_FAILED',
  /** Die Domain ist noch nicht verifiziert und wird deshalb nicht ausgeliefert. */
  domainNotVerified: 'DOMAIN_NOT_VERIFIED',
  /** Der Name gehört der Plattform selbst und darf nicht vergeben werden. */
  domainReserved: 'DOMAIN_RESERVED',
  /** Die Site hat die erlaubte Anzahl an Datenbanken erreicht. */
  databaseLimitReached: 'DATABASE_LIMIT_REACHED',
  /** Die Site hat die erlaubte Anzahl an SFTP-Konten erreicht. */
  sftpLimitReached: 'SFTP_LIMIT_REACHED',
  /**
   * Keine Storage Box kann das Repository dieser Site aufnehmen.
   *
   * Ein eigener Code und nicht `NO_SCHEDULABLE_NODE`: Die Behebung ist eine
   * andere. Hier fehlt kein Server, sondern Platz auf einer Box — und die
   * Bestellung selbst scheitert daran ausdrücklich **nicht**, die Site läuft.
   */
  storageBoxUnavailable: 'STORAGE_BOX_UNAVAILABLE',
  /**
   * Für diese Site ist keine Sicherung eingerichtet.
   *
   * Der Rückweg fragt danach: Eine Site ohne Repository hat nichts, wohin
   * zurückzukehren wäre. Das ist kein `NOT_FOUND` — die Site gibt es.
   */
  backupNotConfigured: 'BACKUP_NOT_CONFIGURED',
  /** Die angeforderte Momentaufnahme steht im Repository dieser Site nicht. */
  snapshotUnknown: 'SNAPSHOT_UNKNOWN',
  /**
   * Das angeforderte Agent-Artefakt liegt auf dem Master nicht bereit.
   *
   * Ein eigener Code, weil die Behebung eine völlig andere ist als bei
   * `NOT_FOUND`: Hier fehlt keine Datenbankzeile, sondern eine Datei im
   * Artefaktverzeichnis des Masters. Ein Worker, der das liest, wartet auf ein
   * Release — er hat selbst nichts falsch gemacht.
   */
  artifactNotFound: 'ARTIFACT_NOT_FOUND',
  /** Ein nachgelagerter Dienst (Worker, Docker, Datenbank) ist nicht erreichbar. */
  upstreamUnavailable: 'UPSTREAM_UNAVAILABLE',
  /** Zu viele Anfragen. */
  rateLimited: 'RATE_LIMITED',
  /** Unerwarteter Fehler — Details stehen nur im Log, nie in der Antwort. */
  internal: 'INTERNAL_ERROR',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export const errorCodeSchema = z.enum([
  ERROR_CODES.validationFailed,
  ERROR_CODES.unauthenticated,
  ERROR_CODES.forbidden,
  ERROR_CODES.notFound,
  ERROR_CODES.conflict,
  ERROR_CODES.enrollmentTokenInvalid,
  ERROR_CODES.protocolVersionUnsupported,
  ERROR_CODES.noSchedulableNode,
  ERROR_CODES.blueprintUnknown,
  ERROR_CODES.siteSpecRejected,
  ERROR_CODES.operationConflict,
  ERROR_CODES.domainAlreadyClaimed,
  ERROR_CODES.dnsVerificationFailed,
  ERROR_CODES.domainNotVerified,
  ERROR_CODES.domainReserved,
  ERROR_CODES.databaseLimitReached,
  ERROR_CODES.sftpLimitReached,
  ERROR_CODES.storageBoxUnavailable,
  ERROR_CODES.backupNotConfigured,
  ERROR_CODES.snapshotUnknown,
  ERROR_CODES.artifactNotFound,
  ERROR_CODES.upstreamUnavailable,
  ERROR_CODES.rateLimited,
  ERROR_CODES.internal,
]);

/** Ein einzelner Feldfehler aus der Schema-Validierung. */
export const fieldErrorSchema = z.object({
  /** Punktnotierter Pfad zum fehlerhaften Feld, z. B. `metrics.memUsedMb`. */
  path: z.string(),
  /** Menschenlesbare Begründung auf Deutsch. */
  message: z.string(),
});

/** Antwortkörper jedes Fehlerfalls. */
export const errorResponseSchema = z.object({
  statusCode: z.number().int(),
  code: errorCodeSchema,
  message: z.string(),
  correlationId: correlationIdSchema,
  timestamp: isoDateTimeSchema,
  /** Nur bei `VALIDATION_FAILED` befüllt. */
  details: z.array(fieldErrorSchema).optional(),
});

export type FieldError = z.infer<typeof fieldErrorSchema>;
export type ErrorResponse = z.infer<typeof errorResponseSchema>;
